/**
 * A labelled segmented button group. Presentation toggles can use it directly; filters use it
 * through `<FilterBar>`, which stages their answer-defining value until Query is pressed.
 */

import { Button } from '@/components/ui/button';
import { useI18n } from '@/lib/i18n';

export function ButtonGroupSelector<TValue extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: readonly { value: TValue; label: string; hint?: string }[];
  value: TValue;
  onChange: (next: TValue) => void;
}) {
  const { text } = useI18n();
  const id = `segmented-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    // gap-0.5: the parent's gap is what separates this group from the next control, not this one.
    <div className="flex flex-col gap-0.5">
      {/* A span, not a <label>: this names a group of buttons, and a <label> with no form field
          to point at is an accessibility warning rather than a label. */}
      <span id={id} className="text-xs font-medium">
        {text(label)}
      </span>
      <div
        role="group"
        aria-labelledby={id}
        // h-9 to match the filter controls it shares a row with — the bar aligns on `items-end`, so
        // a shorter group would drop its label below every other label in the row.
        className="flex h-9 items-center gap-1 rounded-md border bg-muted px-0.5"
      >
        {options.map((option) => (
          <Button
            key={option.value}
            type="button"
            size="sm"
            variant={value === option.value ? 'default' : 'ghost'}
            aria-pressed={value === option.value}
            title={option.hint}
            onClick={() => {
              onChange(option.value);
            }}
            className="h-7 px-2.5 text-xs"
          >
            {text(option.label)}
          </Button>
        ))}
      </div>
    </div>
  );
}
